import React, { useState, useRef } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { ChecklistEditor, ChecklistEditorHandle } from './ChecklistEditor';
import { StepButtonBar } from '../../../EditLegalListWizard/StepButtonBar';
import { useAptorApi } from '../../../../../../../Api';
import { useApiValidationSnackbar } from '../../../../../../../Api/useValidationSnackbar';
import { SelectOption } from '../../../../../../../Components/Form/Form.types';

interface Step2EditChecklistProps {
  lawId: number;
  unitId: number;
  onBack: () => void;
  onCancel: () => void;
  onComplete: () => void;
  updateChecklistInfo?: any;
  currentStepName?: string;
  setUpdateChecklistInfo?: () => void;
  selectedUnits?: SelectOption[];
}

const Step2EditChecklist: React.FC<Step2EditChecklistProps> = ({
  lawId,
  unitId,
  onBack,
  onCancel,
  onComplete,
  updateChecklistInfo,
  currentStepName,
  setUpdateChecklistInfo,
  selectedUnits,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSaving, setIsSaving] = useState(false);
  const checklistEditorRef = useRef<ChecklistEditorHandle>(null);
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(2);
  const handleSave = async () => {
    if (!checklistEditorRef.current) return;

    const saveData = checklistEditorRef.current.getSaveData();
    if (!saveData) return;

    setIsSaving(true);
    try {
      await api.invoke(
        (i) =>
          Promise.all((selectedUnits && selectedUnits.length > 0 ? selectedUnits : [{ value: unitId }]).map((unit) =>
            i.updateCustomerLawChecklist(lawId, unit.value as number, saveData),
          )).then(() => notifySuccess()),
        abortController.current,
        (errors) => notifyValidationErrors(errors),
        notifyUnhandledException
      );

      if (!abortController.current.signal.aborted) {
        onComplete();
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            flexShrink: 0,
            // padding: paperPadding,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            minHeight: '350px',
            overflow: 'auto',
            paddingTop: 0,
          }}
        >
          <ChecklistEditor
            ref={checklistEditorRef}
            lawId={lawId}
            unitId={unitId}
            onComplete={onComplete}
            actions={
              <StepButtonBar
                onCancel={() => { }}
                onBack={() => { }}
                onNext={handleSave}
                nextLabel="form.save"
                nextDisabled={isSaving}
                isLoading={isSaving}
                formatMessage={formatMessage}
                showBack={false}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Step2EditChecklist;
